import React from 'react';
import { connect } from 'react-redux';
import { Box, Button, ButtonBar, InfoLabel } from '../../../../common/components';
import { Col, ControlLabel, Label, Row } from 'react-bootstrap';
import { Field, getFormValues, reduxForm } from 'redux-form';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import BannerPreview from '../BannerPreview/BannerPreview';
import { AmendCampaignEndDateModal } from '../AmendCampaignEndDateModal';
import { openModal } from '../../../../common/components/Modal/ModalRedux';
import CampaignBannerModal from '../CampaignBannerModal/CampaignBannerModal';
import { AddSchemesToCampaignModal } from '../AddSchemesToCampaignModal';
import { compose } from 'redux';
import './CampaignDetails.scss';

/******************************
 Campaign Details - Read Only
 *****************************/

const CampaignDetails = ({
  onEndCampaign,
  onOpenModal,
  onUpdateCampaignBanner,
  resource,
  formValues,
  dispatch,
  change,
}) => {
  const id = resource.data.id;

  const urls = resource?.data?.attributes.urls;
  const resourceAttributes = resource?.data?.attributes;
  const campaignType = resourceAttributes?.campaign_type;
  const includedChannels = getIncludedResource(
    resource.data,
    resource.included,
    'channels',
    'product',
  );

  const channels = Array.isArray(includedChannels) ? includedChannels : [];
  const includedBrokers = getIncludedResource(
    resource.data,
    resource.included,
    'brokers',
  );

  const brokers = Array.isArray(includedBrokers) ? includedBrokers : [];
  const product = getIncludedResource(resource.data, resource.included, 'product');

  const { code, discount, metadata } = formValues?.data?.attributes ?? {};

  const { banner, broker_type: brokerType } = metadata ?? {};

  const {
    enabled: bannerEnabled,
    banner_text: bannerText,
    discount_message: discountMessage,
    banner_info: whereToApply,
    page,
    time_idle: timeIdle,
    phone: bannerPhone,
    files,
  } = banner ?? {};
  const bannerImage = files?.data;

  const status = resourceAttributes.status;
  const editCampaignText =
    status === 'ended'
      ? 'Clone Campaign'
      : status === 'pending'
      ? 'Edit Campaign'
      : 'New Version';

  const showPublicOption = channels.some((channel) =>
    ['B2C', 'B2B'].includes(channel.attributes.channel_type),
  );
  const hasB2CChannel = !!channels.find(
    (channel) => channel.attributes.channel_type === 'B2C',
  );

  const openAmendDateModal = () => {
    onOpenModal('amendCampaignEndDateModal');
  };

  return (
    <Box className={'campaign-details-show'}>
      <Row>
        <Col sm={12}>
          <h2 className="resource-name">
            <Label
              className="pull-right capitalise"
              bsStyle={status === 'active' ? 'success' : 'default'}
            >
              {status}
            </Label>
          </h2>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <InfoLabel value={resourceAttributes.name} label="Name" labelSize={4} />
          <InfoLabel
            name="data.attributes.has_url"
            label="Has url?"
            labelSize={4}
            value={resourceAttributes.has_url ? 'Yes' : 'No'}
          />
          <Row>
            <Col sm={4}>
              <ControlLabel>Url(s)</ControlLabel>
            </Col>
            <Col sm={8}>
              {urls &&
                Object.values(urls).map((attr) => {
                  return <span key={attr.url}>{attr.url}</span>;
                })}
            </Col>
          </Row>

          <InfoLabel label="Code" labelSize={4} value={resourceAttributes.code ?? ''} />
        </Col>
        <Col sm={6}>
          {campaignType === 'Commission Adjustment' ? (
            <InfoLabel
              value={resourceAttributes.premium_overrides.commission_rate}
              label="Commission Rate"
              labelSize={4}
              object
              fieldSize={3}
              addonAfter={'%'}
            />
          ) : (
            <InfoLabel
              value={resourceAttributes?.discount}
              label="Discount"
              labelSize={4}
              fieldSize={3}
              addonAfter={'%'}
            />
          )}
          <InfoLabel
            label="Exclusive"
            labelSize={4}
            value={resourceAttributes?.exclusive ? 'Yes' : 'No'}
          />
          <InfoLabel
            value={resourceAttributes?.from_date}
            date={true}
            label="Start Date"
            labelSize={4}
            fieldSize={7}
          />
          <InfoLabel
            value={resourceAttributes?.to_date}
            date={true}
            label="End Date"
            labelSize={4}
            fieldSize={7}
          />
        </Col>
      </Row>

      <hr id="second-row" />
      <Row>
        <Col sm={6}>
          <InfoLabel
            label="Product"
            labelSize={4}
            value={product?.data?.attributes?.name}
          />
          <InfoLabel
            label="Channel"
            labelSize={4}
            value={channels.map((channel) => channel?.attributes.name).join(', ')}
          />
          {showPublicOption && (
            <InfoLabel
              label="Public?"
              labelSize={4}
              value={resourceAttributes?.is_public ? 'Yes' : 'No'}
            />
          )}
          <InfoLabel
            label="Brokers"
            labelSize={4}
            value={
              <span className={'campaign-details-label'}>
                {brokers.map((broker) => broker?.attributes.name).join(', ')}
              </span>
            }
          />
        </Col>
        <Col sm={6}>
          <InfoLabel
            value={
              <span className={'campaign-details-label'}>
                {resourceAttributes?.criterion?.scheme_type?.equal}
              </span>
            }

            label="Scheme Type"
            labelSize={4}
          />
          <InfoLabel
            value={resourceAttributes?.criterion?.cover_level?.equal}
            label="Cover Level"
            labelSize={4}
          />
        </Col>
      </Row>
      <hr id="third-row" />
      <Row>
        <Col sm={6}>
          <InfoLabel
            label="Family Group"
            labelSize={4}
            value={
              <span className={'campaign-details-label'}>
                {resourceAttributes?.criterion?.traveller_group?.in?.join(', ')}
              </span>
            }
          />
          <InfoLabel
            label="Regions"
            labelSize={4}
            value={
              <span className={'campaign-details-label'}>
                {resourceAttributes?.criterion?.region?.in?.join(', ')}
              </span>
            }
          />
        </Col>
        <Col sm={6}>
          <InfoLabel
            label={'Target Ages'}
            labelSize={4}
            value={`${resourceAttributes?.criterion?.min_age?.min ?? ''} -  ${resource
              ?.criterion?.max_age?.max ?? ''}`}
          />
          <InfoLabel
            label={'Target Duration'}
            labelSize={4}
            value={`${resourceAttributes?.criterion?.duration?.min ?? ''} -  ${resource
              ?.criterion?.duration?.max ?? ''}`}
          />
          <InfoLabel
            label={'Target Healix Scores'}
            labelSize={4}
            value={`${resourceAttributes?.criterion?.max_score?.min ?? ''} -  ${resource
              ?.criterion?.max_score?.max ?? ''}`}
          />
        </Col>
      </Row>
      {!!formValues?.data.attributes.metadata.banner && (
        <div>
          <hr id="third-row" />
          <Row>
            <Col sm={12}>
              <h4>Banner{!bannerEnabled && ': (disabled)'}</h4>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Field
                labelSize={4}
                label="Show on:"
                name={'data.attributes.metadata.banner.page'}
                component={InfoLabel}
              />

              <Field
                labelSize={4}
                label="Time idle"
                name="data.attributes.metadata.banner.time_idle"
                component={InfoLabel}
              />
            </Col>
          </Row>
          <Row>
            <Col
              md={12}
              sm={12}
              xs={12}
              className={`preview-banner-container ${!bannerEnabled && 'disabled'}`}
            >
              <BannerPreview
                code={code}
                discount={discount}
                bannerText={bannerText}
                discountMessage={discountMessage}
                whereToApply={whereToApply}
                page={page}
                timeIdle={timeIdle}
                bannerImage={bannerImage}
                bannerPhone={bannerPhone}
              />
            </Col>
          </Row>
        </div>
      )}

      <AmendCampaignEndDateModal campaignIds={[id]} />

      <ButtonBar>
        <Button
          link
          to={`/admin/campaigns/${id}/edit`}
          className="pull-right pad-right"
          bsStyle="primary"
        >
          {editCampaignText}
        </Button>
        {status !== 'ended' && (
          <Button
            handleClick={openAmendDateModal}
            bsStyle="primary"
            className="pull-right pad-right"
            label="Amend End Date"
          />
        )}

        {status === 'active' && (
          <Button
            handleClick={onEndCampaign}
            bsStyle="primary"
            className="pull-right pad-right"
            label="End Campaign"
          />
        )}

        <AddSchemesToCampaignModal
          campaign={resource}
          productId={product.data.id}
          campaignId={id}
        />
        {status !== 'ended' && hasB2CChannel && (
          <CampaignBannerModal
            code={code}
            discount={discount}
            bannerText={bannerText}
            discountMessage={discountMessage}
            whereToApply={whereToApply}
            page={page}
            timeIdle={timeIdle}
            bannerImage={bannerImage}
            bannerEnabled={bannerEnabled}
            brokerType={brokerType}
            brokers={brokers?.data}
            channels={channels}
            bannerPhone={bannerPhone}
            updateCampaignBanner
            dispatch={dispatch}
            change={change}
            onUpdateCampaignBanner={onUpdateCampaignBanner}
            formValues={formValues}
          />
        )}
      </ButtonBar>
    </Box>
  );
};

const FORM_NAME = 'CampaignDetails';

export default compose(
  connect(
    (state, props) => {
      return {
        formValues: getFormValues(FORM_NAME)(state),
        initialValues: {
          ...props.resource,
          data: {
            ...props.resource.data,
            attributes: {
              ...props.resource.data.attributes,
              metadata: Array.isArray(props.resource.data.attributes.metadata)
                ? {}
                : { ...props.resource.data.attributes.metadata },
            },
          },
        },
        form: FORM_NAME,
      };
    },
    {
      onOpenModal: openModal,
    },
  ),

  reduxForm({}),
)(CampaignDetails);
